import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const parseSpaceIdentifier = (input) => {
  if (!input) return '';
  const match = String(input).trim().match(UUID_RE);
  return match ? match[0] : '';
};

// 🔒 09-24: the pasted space can be a PRIVATE ported room, which only takes
// invites from a seat holder. This browser's seat for it (the same two keys
// GameStepPage and chatGameApi read) rides along; {} when there is none.
const seatCredsFor = (gameId) => {
  try {
    const playerId = localStorage.getItem(`humanPlayerId_${gameId}`);
    const secret = playerId ? localStorage.getItem(`player_secret_${playerId}`) : null;
    return playerId && secret ? { player_id: playerId, player_secret: secret } : {};
  } catch { return {}; }
};

const LaunchAgentModal = ({ agent, onClose }) => {
  const navigate = useNavigate();
  const [inviteInput, setInviteInput] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(null);
  // Inline invite error (was a native alert()); cleared on retype/close.
  const [inviteError, setInviteError] = useState(null);
  const [resume, setResume] = useState(null); // career track record from the profile endpoint

  // Fetch the agent's résumé (spaces, ships, latest quote) when the modal opens
  useEffect(() => {
    if (!agent?.id) { setResume(null); return; }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/agents/${agent.id}`)
      .then(r => r.json())
      .then(result => { if (!cancelled) setResume(result?.agent?.resume || null); })
      .catch(() => { if (!cancelled) setResume(null); });
    return () => { cancelled = true; };
  }, [agent?.id]);

  if (!agent) return null;

  const close = () => {
    setInviteInput('');
    setInviteSuccess(null);
    setInviteError(null);
    setIsInviting(false);
    onClose && onClose();
  };

  const handleInvite = async () => {
    const gameId = parseSpaceIdentifier(inviteInput);
    if (!gameId) {
      setInviteError("That doesn't look like a valid space URL or ID. Paste a full space URL or its UUID.");
      return;
    }
    setInviteError(null);
    setIsInviting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/${agent.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ game_id: gameId, ...seatCredsFor(gameId) }),
      });
      const result = await response.json();
      if (result.success) {
        setInviteInput('');
        // 📄 10-01 batch 3 (#25): a connected agent gets the invite and joins when it checks in
        // 📄 10-01 batch 2b: our own words, so an older server's wording never shows
        setInviteSuccess({ gameId, note: result.seated === false ? `${agent.name} gets the invite next time it checks in, and joins if it wants.` : null });
      } else {
        setInviteError(result.error || 'Failed to invite agent. Try again in a moment.');
      }
    } catch (err) {
      setInviteError('Failed to invite agent. Check your connection and try again.');
    } finally {
      setIsInviting(false);
    }
  };

  const resolvedSpaceId = parseSpaceIdentifier(inviteInput);
  const inputLooksInvalid = inviteInput.trim() && !resolvedSpaceId;

  return (
    <div style={styles.modalOverlay} onClick={close}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button onClick={close} style={styles.modalClose} aria-label="Close">&#10005;</button>

        {/* Agent header — full-width hero art when we have it. Height is
            CAPPED (was square, 1:1): at 420px wide a square hero alone ate
            most of the 90vh budget and pushed the invite/create actions
            below the fold on desktop (Nina 08-13). */}
        {agent.avatar_url && (
          <div style={{
            width: '100%', height: 180, borderRadius: 12,
            overflow: 'hidden', marginBottom: 12, background: 'rgba(123, 31, 162, 0.1)',
            flexShrink: 0,
          }}>
            <img src={agent.avatar_url} alt={agent.name} style={styles.avatarImg} />
          </div>
        )}
        <div style={styles.modalAgentHeader}>
          {!agent.avatar_url && (
            <div style={styles.avatar}>
              <span style={styles.avatarEmoji}>&#129302;</span>
            </div>
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>{agent.name}</div>
            <span style={agent.agent_type === 'connected' ? styles.cardTypeBadgeConnected : styles.cardTypeBadgeImported}>
              {agent.agent_type === 'connected' ? '\u{1F517} Connected' : '⚡ Auto-plays'}
            </span>
          </div>
        </div>

        {/* Who they are — persona blurb (metadata.appearance; the raw image
            prompt lives in metadata.art_prompt since 08-01) */}
        {agent.appearance && (
          <div style={{
            margin: '0 0 12px', fontSize: 12.5, fontStyle: 'italic',
            color: 'rgba(255,255,255,0.55)', lineHeight: 1.4,
          }}>
            ✨ {agent.appearance}
          </div>
        )}

        {/* Career résumé — the agent's track record across spaces */}
        {resume && (resume.spaces?.length > 0 || resume.messages_sent > 0) && (
          <div style={{
            margin: '0 0 14px', padding: '12px 14px', borderRadius: 10,
            backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
              📇 Career
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12.5, color: 'rgba(255,255,255,0.8)', marginBottom: resume.current_ships?.length || resume.latest_quote ? 8 : 0 }}>
              <span>🏠 {resume.spaces.length} space{resume.spaces.length !== 1 ? 's' : ''}</span>
              <span>💬 {resume.messages_sent} message{resume.messages_sent !== 1 ? 's' : ''}</span>
            </div>
            {(resume.current_ships || []).map((ship, i) => (
              <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 4, lineHeight: 1.4 }}>
                💘 <strong>{ship.ship_name}</strong>{ship.status ? ` · ${ship.status}` : ''}
                <span style={{ color: 'rgba(255,255,255,0.45)' }}> · {ship.space}</span>
              </div>
            ))}
            {resume.latest_quote && (
              <div style={{ fontSize: 12, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', marginTop: 6, lineHeight: 1.45 }}>
                &ldquo;{resume.latest_quote.message}&rdquo;
                {resume.latest_quote.space && <span style={{ fontStyle: 'normal' }}> · in {resume.latest_quote.space}</span>}
              </div>
            )}
            {resume.spaces?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                {resume.spaces.slice(0, 5).map((s, i) => (
                  <span key={i} style={{
                    fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 8,
                    backgroundColor: 'rgba(139,92,246,0.15)', color: '#b9a3f5', border: '1px solid rgba(139,92,246,0.3)',
                  }}>{s.name}</span>
                ))}
              </div>
            )}
          </div>
        )}
        {resume && !(resume.spaces?.length > 0 || resume.messages_sent > 0) && (
          <div style={{ margin: '0 0 14px', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
            ✨ Fresh face, no appearances yet. Be their first space!
          </div>
        )}

        {/* Success state */}
        {inviteSuccess ? (
          <div style={styles.successBanner}>
            <div style={styles.successCheck}>&#10003;</div>
            <div style={styles.successText}>{agent.name} invited!</div>
            {inviteSuccess.note && <div style={styles.successNote}>{inviteSuccess.note}</div>}{/* 📄 10-01 batch 3 */}
            <button
              onClick={() => { window.open(`/game-step?gameId=${inviteSuccess.gameId}`, '_blank'); close(); }}
              style={styles.successGoButton}
            >
              Go to Space &rarr;
            </button>
            <button onClick={() => setInviteSuccess(null)} style={styles.successDismiss}>
              Invite to another space
            </button>
          </div>
        ) : (
          <>
            {/* Section 1: Invite to existing space */}
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}>Invite to existing space</div>
              <input
                type="text"
                placeholder="Paste space URL..."
                value={inviteInput}
                autoFocus={typeof window === 'undefined' || window.innerWidth >= 768}
                onChange={(e) => { setInviteInput(e.target.value); setInviteError(null); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && resolvedSpaceId && !isInviting) {
                    handleInvite();
                  } else if (e.key === 'Escape') {
                    close();
                  }
                }}
                style={styles.inviteInput}
              />
              {resolvedSpaceId && (
                <div style={styles.inviteResolved}>
                  <span style={styles.inviteResolvedCheck}>&#10003;</span>
                  Space ID: <code style={styles.inviteHintCode}>{resolvedSpaceId.substring(0, 8)}&hellip;</code>
                </div>
              )}
              {inputLooksInvalid && (
                <div style={styles.inviteInvalid}>
                  Couldn&rsquo;t find a space ID in that. Paste a full space URL.
                </div>
              )}
              {inviteError && (
                <div style={styles.inviteInvalid}>{inviteError}</div>
              )}
              <button
                onClick={handleInvite}
                disabled={!resolvedSpaceId || isInviting}
                style={{
                  ...styles.inviteConfirmButton,
                  opacity: (!resolvedSpaceId || isInviting) ? 0.5 : 1,
                }}
              >
                {isInviting ? 'Inviting...' : 'Invite to Space'}
              </button>
            </div>

            {/* Divider */}
            <div style={styles.launchDivider}>
              <span style={styles.launchDividerLine} />
              <span style={styles.launchDividerText}>or</span>
              <span style={styles.launchDividerLine} />
            </div>

            {/* Section 2: Create new space */}
            <div style={styles.modalSection}>
              <button
                onClick={() => navigate(`/custom-space?agentId=${agent.id}`)}
                style={styles.createWithAgentButton}
              >
                Create New Space with {agent.name}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    position: 'relative',
    background: 'linear-gradient(160deg, #1e1e3a 0%, #2a1a50 100%)',
    borderRadius: 16,
    padding: '32px 28px',
    width: '100%',
    maxWidth: 420,
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  modalClose: {
    position: 'absolute',
    top: 12,
    right: 14,
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    fontSize: 18,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 4,
    lineHeight: 1,
  },
  modalAgentHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 24,
  },
  modalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  modalSectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.6)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'rgba(123, 31, 162, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarEmoji: {
    fontSize: 22,
  },
  cardTypeBadgeImported: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#7b1fa2',
    background: 'rgba(123, 31, 162, 0.08)',
    padding: '2px 7px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
  },
  cardTypeBadgeConnected: {
    fontSize: 10.5,
    fontWeight: 700,
    color: '#2563eb',
    background: 'rgba(37, 99, 235, 0.08)',
    padding: '2px 7px',
    borderRadius: 10,
    whiteSpace: 'nowrap',
  },
  inviteInput: {
    width: '100%',
    minWidth: 0,
    padding: '9px 12px',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#111',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  inviteResolved: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 12,
    color: '#15803d',
    fontWeight: 600,
  },
  inviteResolvedCheck: {
    fontSize: 13,
    color: '#15803d',
  },
  inviteInvalid: {
    fontSize: 12,
    color: '#b91c1c',
    textAlign: 'center',
    lineHeight: 1.4,
  },
  inviteHintCode: {
    background: '#f4f4f5',
    padding: '1px 5px',
    borderRadius: 3,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    fontSize: 10.5,
    color: '#be185d',
    border: '1px solid #e4e4e7',
  },
  successBanner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '12px 0 4px',
  },
  successCheck: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 700,
    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.35)',
  },
  successText: {
    fontSize: 14,
    fontWeight: 700,
    color: '#22c55e',
  },
  successNote: { // 📄 10-01 batch 3
    fontSize: 12,
    lineHeight: 1.4,
    color: 'rgba(255,255,255,0.7)',
    textAlign: 'center',
  },
  successGoButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(34, 197, 94, 0.3)',
    transition: 'all 0.2s',
  },
  successDismiss: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.45)',
    fontSize: 12,
    cursor: 'pointer',
    padding: '2px 8px',
  },
  createWithAgentButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #7b1fa2, #4f46e5)',
    color: '#fff',
    fontSize: 13.5,
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    boxShadow: '0 3px 10px rgba(123, 31, 162, 0.3)',
    transition: 'all 0.2s',
  },
  launchDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '10px 0',
  },
  launchDividerLine: {
    flex: 1,
    height: 1,
    background: '#e4e4e7',
  },
  launchDividerText: {
    fontSize: 11.5,
    color: '#999',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  inviteConfirmButton: {
    padding: '9px 22px',
    borderRadius: 6,
    border: 'none',
    background: '#7b1fa2',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

export default LaunchAgentModal;
